// Performance optimization utilities

/**
 * Debounce function to limit the rate of function execution
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @param {boolean} immediate - Execute immediately on first call
 * @returns {Function} Debounced function
 */
export const debounce = (func, wait, immediate = false) => {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      timeout = null
      if (!immediate) func(...args)
    }
    const callNow = immediate && !timeout
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
    if (callNow) func(...args)
  }
}

/**
 * Throttle function to limit function execution frequency
 * @param {Function} func - Function to throttle
 * @param {number} limit - Time limit in milliseconds
 * @returns {Function} Throttled function
 */
export const throttle = (func, limit) => {
  let inThrottle
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args)
      inThrottle = true
      setTimeout(() => inThrottle = false, limit)
    }
  }
}

/**
 * Memoization utility for expensive computations
 * @param {Function} fn - Function to memoize
 * @param {Function} keyGenerator - Function to generate cache key
 * @returns {Function} Memoized function
 */
export const memoize = (fn, keyGenerator = (...args) => JSON.stringify(args)) => {
  const cache = new Map()
  
  return function(...args) {
    const key = keyGenerator(...args)
    
    if (cache.has(key)) {
      return cache.get(key)
    }
    
    const result = fn.apply(this, args)
    cache.set(key, result)
    
    // Prevent memory leaks by limiting cache size
    if (cache.size > 100) {
      const firstKey = cache.keys().next().value
      cache.delete(firstKey)
    }
    
    return result
  }
}

/**
 * Performance measurement utility
 * @param {string} name - Operation name
 * @param {Function} operation - Function to measure
 * @returns {Promise} Operation result with performance data
 */
export const measureAsync = async (name, operation) => {
  const start = performance.now()
  
  try {
    const result = await operation()
    const end = performance.now()
    const duration = end - start
    
    console.log(`⚡ ${name} completed in ${duration.toFixed(2)}ms`)
    
    // Log warning for slow operations
    if (duration > 2000) {
      console.warn(`🐌 Slow operation detected: ${name} took ${duration.toFixed(2)}ms`)
    }
    
    return { result, duration, success: true }
  } catch (error) {
    const end = performance.now()
    const duration = end - start
    
    console.error(`❌ ${name} failed after ${duration.toFixed(2)}ms:`, error)
    
    return { error, duration, success: false }
  }
}

/**
 * Synchronous performance measurement
 * @param {string} name - Operation name
 * @param {Function} operation - Function to measure
 * @returns {*} Operation result with performance data
 */
export const measureSync = (name, operation) => {
  const start = performance.now()
  
  try {
    const result = operation()
    const end = performance.now()
    const duration = end - start
    
    console.log(`⚡ ${name} completed in ${duration.toFixed(2)}ms`)
    
    return { result, duration, success: true }
  } catch (error) {
    const end = performance.now()
    const duration = end - start
    
    console.error(`❌ ${name} failed after ${duration.toFixed(2)}ms:`, error)
    
    return { error, duration, success: false }
  }
}

/**
 * Simple cache implementation with TTL
 */
export class SimpleCache {
  constructor(defaultTTL = 5 * 60 * 1000) { // 5 minutes default
    this.cache = new Map()
    this.defaultTTL = defaultTTL
  }
  
  set(key, value, ttl = this.defaultTTL) {
    const expiry = Date.now() + ttl
    this.cache.set(key, { value, expiry })
    
    // Clean up expired entries periodically
    if (this.cache.size % 10 === 0) {
      this.cleanup()
    }
  }
  
  get(key) {
    const item = this.cache.get(key)
    
    if (!item) {
      return null
    }
    
    if (Date.now() > item.expiry) {
      this.cache.delete(key)
      return null
    }
    
    return item.value
  }
  
  has(key) {
    return this.get(key) !== null
  }
  
  delete(key) {
    return this.cache.delete(key)
  }
  
  clear() {
    this.cache.clear()
  }
  
  cleanup() {
    const now = Date.now()
    for (const [key, item] of this.cache.entries()) {
      if (now > item.expiry) {
        this.cache.delete(key)
      }
    }
  }
  
  size() {
    this.cleanup()
    return this.cache.size
  }
}

/**
 * Resource loading utility with timeout and retry
 * @param {Function} loader - Function that returns a Promise
 * @param {Object} options - Configuration options
 * @returns {Promise} Loaded resource
 */
export const loadWithRetry = async (loader, options = {}) => {
  const {
    maxRetries = 3,
    timeout = 10000,
    retryDelay = 1000,
    backoff = true
  } = options
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // Create timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Operation timeout')), timeout)
      })
      
      // Race between loader and timeout
      const result = await Promise.race([loader(), timeoutPromise])
      
      return result
    } catch (error) {
      console.warn(`Attempt ${attempt} failed:`, error.message)
      
      if (attempt === maxRetries) {
        throw new Error(`Failed after ${maxRetries} attempts: ${error.message}`)
      }
      
      // Calculate delay with optional exponential backoff
      const delay = backoff ? retryDelay * Math.pow(2, attempt - 1) : retryDelay
      await new Promise(resolve => setTimeout(resolve, delay))
    }
  }
}

export default {
  debounce,
  throttle,
  memoize,
  measureAsync,
  measureSync,
  SimpleCache,
  loadWithRetry
}